import { useEffect, useMemo, useRef, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import {
  CREATE_TASK_TOOL_NAME,
  createdTaskReferenceFromResult,
  type CreatedTaskReference,
} from "@/lib/created-task";
import type { RenderItem } from "@/lib/render-items";
import {
  detailSourceKey,
  isToolDetailsResponse,
  type HistoryToolDetailSource,
} from "@/lib/tool-details";

type ToolItem = Extract<RenderItem, { kind: "tool" }>;

function collectToolItems(items: readonly RenderItem[]): ToolItem[] {
  const tools: ToolItem[] = [];

  for (const item of items) {
    if (item.kind === "tool") {
      tools.push(item);
    } else if (item.kind === "tool-group") {
      tools.push(...collectToolItems(item.items));
    }
  }

  return tools;
}

function isCompletedCreateTask(tool: ToolItem): boolean {
  return tool.name === CREATE_TASK_TOOL_NAME && tool.status === "end";
}

/**
 * Resolve only the task references needed to render `create_task` pills.
 *
 * Live tool markers already carry their terminal result, so they never make
 * a request. Persisted message rows intentionally omit tool content; for
 * those completed calls we use the existing narrow tool-details endpoint.
 * Failure, malformed data, or a stale response simply leaves the call
 * without a pill.
 */
export function useCreatedTaskReferences(
  sessionId: string,
  items: readonly RenderItem[],
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
): ReadonlyMap<string, CreatedTaskReference> {
  const toolItems = useMemo(() => collectToolItems(items), [items]);
  const historySources = useMemo(() => {
    const sources = new Map<string, HistoryToolDetailSource>();

    for (const tool of toolItems) {
      if (!isCompletedCreateTask(tool) || tool.source.kind !== "history") continue;
      sources.set(detailSourceKey(tool.source), tool.source);
    }

    return [...sources.values()];
  }, [toolItems]);
  const referencesBySourceRef = useRef(new Map<string, CreatedTaskReference | null>());
  const controllersRef = useRef(new Map<string, AbortController>());
  const sessionRef = useRef(sessionId);
  const [revision, setRevision] = useState(0);
  const backendReconnectKey = useBackendReconnectKey();

  useEffect(() => {
    sessionRef.current = sessionId;
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current.clear();
    referencesBySourceRef.current.clear();
    setRevision((current) => current + 1);

    return () => {
      for (const controller of controllersRef.current.values()) controller.abort();
      controllersRef.current.clear();
    };
  }, [backendReconnectKey, sessionId]);

  useEffect(() => {
    for (const source of historySources) {
      if (source.sessionId !== sessionId) continue;

      const key = detailSourceKey(source);
      if (
        referencesBySourceRef.current.has(key)
        || controllersRef.current.has(key)
      ) {
        continue;
      }

      const controller = new AbortController();
      controllersRef.current.set(key, controller);

      void (async () => {
        let task: CreatedTaskReference | null = null;

        try {
          const response = await fetchWithAuth(
            `/api/sessions/${encodeURIComponent(source.sessionId)}/messages/${encodeURIComponent(source.messageId)}/tool-details`,
            { headers: {}, signal: controller.signal },
          );
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const payload: unknown = await response.json();
          if (
            isToolDetailsResponse(payload)
            && payload.message_id === source.messageId
            && payload.tool_name === CREATE_TASK_TOOL_NAME
            && payload.result.state === "available"
          ) {
            task = createdTaskReferenceFromResult(payload.result.value);
          }
        } catch {
          // A task pill is supplementary UI. Tool detail failures are handled
          // by the regular disclosure path and must not disrupt chat rendering.
        }

        if (
          controller.signal.aborted
          || sessionRef.current !== source.sessionId
          || controllersRef.current.get(key) !== controller
        ) {
          return;
        }

        controllersRef.current.delete(key);
        referencesBySourceRef.current.set(key, task);
        setRevision((current) => current + 1);
      })();
    }
  }, [backendReconnectKey, fetchWithAuth, historySources, sessionId]);

  return useMemo(() => {
    const references = new Map<string, CreatedTaskReference>();

    for (const tool of toolItems) {
      if (!isCompletedCreateTask(tool)) continue;

      if (tool.source.kind === "live") {
        const result = tool.source.details.result;
        const task = result.state === "available"
          ? createdTaskReferenceFromResult(result.value)
          : null;
        if (task) references.set(tool.id, task);
      } else if (tool.source.kind === "history") {
        const task = referencesBySourceRef.current.get(detailSourceKey(tool.source));
        if (task) references.set(tool.id, task);
      }
    }

    return references;
  }, [revision, toolItems]);
}
